import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { useWindowSize } from "@utils/hooks";
import { joinClassNames } from "@utils/helpers";
import { useIsShopsite } from "@context/DomainContext";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/popovers/dialog";
import { Button, Card, Heading, Text, Tooltip } from "@components/theming";
import DialogCloseButton from "@components/popovers/dialogCloseButton";
import { useTranslations } from "next-intl";

interface Props {
  title: string;
  description: string;
  icon?: React.ReactNode;
  isWarning?: boolean;
}

const EventToolTip = ({
  title,
  description,
  icon,
  isWarning = false,
}: Props) => {
  const windowSize = useWindowSize();
  const [open, setOpen] = useState(false);
  const isShopsite = useIsShopsite();
  const utilTranslations = useTranslations("Utils");

  return windowSize.width && windowSize.width > 640 ? (
    <Tooltip
      side="bottom"
      sideOffset={10}
      shopsiteClasses={joinClassNames(
        "max-w-96",
        isWarning ? "!bg-amber-100" : ""
      )}
      arrowClasses={isWarning ? "!fill-amber-100" : "fill-indigo-100"}
      content={
        <>
          <Text
            as="span"
            sharedClasses="block"
            shopsiteClasses={joinClassNames(
              "text-sm",
              isWarning ? "text-amber-700" : "text-indigo-700"
            )}
          >
            {description}
          </Text>
        </>
      }
    >
      <QuestionMarkCircleIcon
        className="w-5 h-5"
        aria-label={`${title}-button`}
      />
    </Tooltip>
  ) : (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <QuestionMarkCircleIcon
          className="w-5 h-5"
          aria-label={`${title}-button`}
        />
      </DialogTrigger>
      <AnimatePresence mode="wait">
        {open && (
          <DialogContent
            forceMount
            handleClose={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          >
            <Card asChild>
              <motion.div
                initial="hidden"
                animate="show"
                exit="hidden"
                className="flex flex-col"
                transition={{ duration: 0.2 }}
                variants={{
                  hidden: {
                    y: "100%",
                    opacity: 0,
                  },
                  show: {
                    y: 0,
                    opacity: 1,
                  },
                }}
              >
                <div className="flex flex-col">
                  <div
                    className={joinClassNames(
                      "flex flex-col gap-4 pb-4",
                      isShopsite ? "px-6 pt-6" : "",
                      isShopsite
                        ? isWarning
                          ? "bg-amber-500"
                          : "bg-indigo-500"
                        : ""
                    )}
                  >
                    <div className="relative self-end">
                      <DialogClose
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        asChild
                      >
                        <DialogCloseButton shopsiteVariant="white" />
                      </DialogClose>
                    </div>
                    <DialogHeader className="!text-left">
                      <DialogTitle asChild>
                        <Heading
                          as="h3"
                          size="5"
                          weight="bold"
                          shopsiteClasses="text-white text-xl font-bold"
                          sharedClasses="relative z-10"
                        >
                          {title}
                        </Heading>
                      </DialogTitle>
                      {icon ?? (
                        <QuestionMarkCircleIcon
                          className={joinClassNames(
                            "absolute top-0 left-4 w-24 h-24 -rotate-12",
                            isShopsite
                              ? "text-white/10"
                              : "text-[color:var(--accent-a3)]"
                          )}
                        />
                      )}
                    </DialogHeader>
                  </div>
                  <Text shopsiteClasses="px-6 pt-4 text-gray-600">
                    {description}
                  </Text>
                  <div className={isShopsite ? "px-6 pb-6" : ""}>
                    <DialogClose
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      asChild
                    >
                      <Button
                        sharedClasses="mt-4 w-full"
                        shopsiteClasses={
                          isWarning
                            ? "!bg-amber-500 hover:!bg-amber-400"
                            : undefined
                        }
                      >
                        {utilTranslations("done")}
                      </Button>
                    </DialogClose>
                  </div>
                </div>
              </motion.div>
            </Card>
          </DialogContent>
        )}
      </AnimatePresence>
    </Dialog>
  );
};

export default EventToolTip;
